'use client';

import * as React from 'react';
import { cn } from './lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { Dialog as SheetPrimitive } from 'radix-ui';

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/30 [backdrop-filter:blur(4px)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
}

const sheetVariants = cva(
  'flex flex-col items-strech fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-400',
  {
    variants: {
      side: {
        top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
        bottom:
          'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
        left: 'inset-y-0 start-0 h-full w-3/4 border-e data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm rtl:data-[state=closed]:slide-out-to-right rtl:data-[state=open]:slide-in-from-right',
        right:
          'inset-y-0 end-0 h-full w-3/4  border-s data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm rtl:data-[state=closed]:slide-out-to-left rtl:data-[state=open]:slide-in-from-left',
      },
      variant: {
      /** Edge-to-edge drawer. Navigation menus and anything pinned to the viewport edge. */
      drawer: '',
      /**
       * The standard form sheet: a floating rounded panel inset from the edges,
       * with a ruled header and footer. Create New Deal is the reference, and
       * ten sheets had each pasted the same ~100 characters of utilities to get
       * it — so the shape, the close-button offset and the header/footer chrome
       * all live here now and a consumer declares only its width.
       *
       * The header/footer/title rules are descendant selectors rather than
       * defaults on those components, so `drawer` sheets keep the plain chrome.
       */
      panel: [
        'gap-0 inset-5 start-auto h-auto rounded-lg border p-0 sm:max-w-none',
        '[&_[data-slot=sheet-close]]:top-4.5 [&_[data-slot=sheet-close]]:end-5',
        '[&_[data-slot=sheet-header]]:border-b [&_[data-slot=sheet-header]]:border-border',
        '[&_[data-slot=sheet-header]]:px-5 [&_[data-slot=sheet-header]]:py-3',
        '[&_[data-slot=sheet-title]]:text-sm [&_[data-slot=sheet-title]]:font-medium',
        '[&_[data-slot=sheet-footer]]:flex-row [&_[data-slot=sheet-footer]]:justify-end',
        '[&_[data-slot=sheet-footer]]:border-t [&_[data-slot=sheet-footer]]:border-border',
        '[&_[data-slot=sheet-footer]]:px-5 [&_[data-slot=sheet-footer]]:py-2.5',
        '[&_[data-slot=sheet-footer]]:gap-2.5',
      ].join(' '),
      },
    },
    defaultVariants: {
      side: 'right',
      variant: 'drawer',
    },
  },
);

interface SheetContentProps
  extends React.ComponentProps<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  overlay?: boolean;
  close?: boolean;
}

function SheetContent({
  side = 'right',
  variant = 'drawer',
  overlay = true,
  close = true,
  className,
  children,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & SheetContentProps) {
  return (
    <SheetPortal>
      {overlay && <SheetOverlay />}
      <SheetPrimitive.Content className={cn(sheetVariants({ side, variant }), className)} {...props}>
        {children}
        {close && (
          <SheetPrimitive.Close
            data-slot="sheet-close"
            className="cursor-pointer absolute end-5 top-4 rounded-sm opacity-60 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-header"
      className={cn('flex flex-col space-y-1 text-center sm:text-start', className)}
      {...props}
    />
  );
}

/**
 * The sheet's scrolling region.
 *
 * SheetContent is a fixed, full-height flex column, so the body is the only
 * child that may absorb overflow — it has to be a flex item that can shrink
 * (`min-h-0`, without which a flex child never shrinks below its content) and
 * that scrolls its own overflow.
 *
 * It used to be a bare `py-2.5` div with neither, so a body taller than the
 * sheet simply overflowed with no way to reach the rest: LogActionSheet and
 * LeadActionFormSheet were unreachable past the fold once a follow-up task was
 * added. Every other sheet worked around it independently — some wrapping the
 * content in a ScrollArea pinned to `h-[calc(100dvh-10rem)]` (a magic number
 * that silently breaks the moment the header or footer changes height), others
 * setting `overflow-y-auto` by hand. Owning it here is what makes those
 * workarounds removable and every sheet scroll the same way.
 */
function SheetBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-body"
      className={cn('flex-1 min-h-0 overflow-y-auto py-2.5', className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
      {...props}
    />
  );
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('text-base font-semibold text-foreground', className)}
      {...props}
    />
  );
}

function SheetDescription({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
